import {
  BoatingRiskRating,
  VesselGraphic,
  WaterBody,
  calculateWindSea,
  fetchForWaterBody,
} from '@deckee/utilities';
import type { HourlyMarineConditions } from '@deckee/utilities';

/**
 * The per-moment description under the status word.
 *
 * One concise line per watercraft graphic and rating, describing what it is
 * actually like out there. The lines deliberately avoid naming the craft or its
 * length, because both are already shown above them.
 *
 * There are two sets: the coastal set assumes real ocean swell, while the
 * inland set describes wind and the short, steep chop it raises.
 */

type RideCopyByRating = Readonly<Record<BoatingRiskRating, string>>;

const COASTAL_COPY: Readonly<Record<VesselGraphic, RideCopyByRating>> = {
  [VesselGraphic.Kayak]: {
    green:
      "Settled water with small waves. You'll paddle with stable, controlled strokes.",
    amber:
      'Choppy and unstable. Staying upright gets difficult and you could easily capsize.',
    red: 'Breaking waves will easily capsize you. Far too rough to be out there.',
  },
  [VesselGraphic.JetSki]: {
    green:
      "Settled water with small waves. You'll get a fast ride with good stability.",
    amber:
      "Choppy and jarring. You'll be bounced around and soaked, and conditions could quickly turn unsafe.",
    red: 'Violent, breaking seas will throw you off. Stay off the water.',
  },
  [VesselGraphic.MotorSmall]: {
    green:
      'A gentle swell running. Your boat rides over it with minimal spray on board.',
    amber:
      'Lumpy and pitching. Your boat gets knocked around and conditions may not be safe.',
    red: 'Heavy, breaking seas put you at real risk of swamping. Stay ashore.',
  },
  [VesselGraphic.MotorMedium]: {
    green:
      'A gentle swell running. Your boat rides it steadily with the odd bit of spray.',
    amber:
      'Bumpy and lively. Your boat pitches and rolls and conditions may not be safe.',
    red: "Steep, breaking seas with your bow slamming hard. Don't head out.",
  },
  [VesselGraphic.MotorLarge]: {
    green:
      'A gentle swell running. Your boat rides it comfortably with little movement.',
    amber:
      'Noticeable roll and pitch. Your ride gets bumpy and conditions could become unsafe.',
    red: 'Big, breaking seas with heavy spray and pounding. Far too rough to head out.',
  },
  [VesselGraphic.Sail]: {
    green:
      'A steady breeze and gentle swell. The boat sails with some heel and tracks well.',
    amber:
      'Reefed and sailing hard. Expect plenty of heel and spray as the wind and sea build.',
    red: 'Beyond safe sailing, overpowering even under reefed sail. A real risk of a knockdown, so stay ashore.',
  },
};

const INLAND_COPY: Readonly<Record<VesselGraphic, RideCopyByRating>> = {
  [VesselGraphic.Kayak]: {
    green:
      "Light wind with the water rippled. You'll paddle with stable, controlled strokes.",
    amber:
      'The wind has the water choppy. Wind waves build fast and staying upright takes real effort.',
    red: "Strong wind and steep wind chop. You'll struggle to make way and could easily be blown over or capsized.",
  },
  [VesselGraphic.JetSki]: {
    green:
      "Light wind with the water rippled. You'll get a fast ride with good stability.",
    amber:
      "Wind chop is building. You'll be bounced around and the ride turns jarring and tiring.",
    red: "Strong wind whips up steep chop. You'll be thrown about hard, even on sheltered water.",
  },
  [VesselGraphic.MotorSmall]: {
    green:
      'Light wind with some chop. Your boat sits steady and holds its line.',
    amber:
      'The wind has kicked up a short, sharp chop. Your boat gets knocked about and handling suffers.',
    red: 'Strong wind and steep wind waves. Your boat is hard to control, even on sheltered water.',
  },
  [VesselGraphic.MotorMedium]: {
    green: 'Light wind with some chop. Your boat cruises steadily through it.',
    amber:
      'Building wind brings a short chop. Your boat pitches and slaps and the ride gets uncomfortable.',
    red: "Strong wind drives steep, breaking chop. Your boat is knocked around hard. Don't head out.",
  },
  [VesselGraphic.MotorLarge]: {
    green:
      'Light wind with some chop. Your boat rides it comfortably with little movement.',
    amber:
      "Wind chop is building. You'll feel the slap and roll and the ride turns bumpy.",
    red: 'Strong wind whips up steep, breaking chop. Even sheltered water turns rough and unsafe.',
  },
  [VesselGraphic.Sail]: {
    green:
      'A light, workable breeze. The boat sails with some heel and tracks well.',
    amber:
      "Fresh, gusty wind. You'll be reefed and sailing hard, with plenty of heel and spray.",
    red: "Near gale-force wind. You'll be overpowered even reefed, with a real risk of a knockdown. Stay ashore.",
  },
};

/**
 * Appended to every inland forecast, so the reader knows what the numbers do
 * and do not cover.
 *
 * Followed (when a nearby swell station is available) by
 * {@link INLAND_NEAREST_COASTAL_PREFIX} plus a linked place name.
 */
export const INLAND_FORECAST_CAVEAT =
  'This forecast reflects wind only and assumes large, exposed waters.';

/** Leads the linked nearest coastal place name on inland forecasts. */
export const INLAND_NEAREST_COASTAL_PREFIX =
  'The nearest coastal swell forecast is';

/**
 * The line describing what the moment is like for this craft.
 *
 * Both the coastal and inland lines are keyed on the SAME rating the colour
 * band and status word use, so the description can never contradict the colour.
 * (The prototype keyed its inland line on wind severity alone, which could
 * disagree with the inland colour once wind chop rated in its own right.)
 */
export const getRideCopy = (
  graphic: VesselGraphic,
  rating: BoatingRiskRating,
  waterBody: WaterBody,
): string => {
  const copy = waterBody === WaterBody.Coast ? COASTAL_COPY : INLAND_COPY;

  return (copy[graphic] ?? copy[VesselGraphic.MotorMedium])[rating];
};

/**
 * The significant sea height for the moment: swell combined with the wind's own
 * sea on the coast, and the wind chop alone inland.
 */
export const calculateSignificantSeaHeightM = (
  conditions: HourlyMarineConditions,
  waterBody: WaterBody,
): number => {
  const windSeaHeightM = calculateWindSea(
    conditions.windSpeedMps,
    fetchForWaterBody(waterBody),
  ).heightM;

  if (waterBody !== WaterBody.Coast) {
    return windSeaHeightM;
  }

  return Math.sqrt(
    conditions.swellHeightM * conditions.swellHeightM +
      windSeaHeightM * windSeaHeightM,
  );
};

const formatMetres = (metres: number): string => metres.toFixed(1);

/** Feet are the metric value converted, dropping the decimal once past 10 ft. */
const formatFeet = (metres: number): string => {
  const feet = metres * 3.28084;

  return feet > 10 ? String(Math.round(feet)) : feet.toFixed(1);
};

const formatHeight = (metres: number): string =>
  `${formatMetres(metres)}m (${formatFeet(metres)}ft)`;

/** Below this the inland chop is not worth quoting a number for. */
const NEGLIGIBLE_INLAND_MAX_WAVE_M = 0.3;

/**
 * The wave-height sentence woven into the copy. The largest waves run to about
 * twice the significant height.
 */
export const getHeightClause = (
  conditions: HourlyMarineConditions,
  waterBody: WaterBody,
): string => {
  const significantM = calculateSignificantSeaHeightM(conditions, waterBody);
  const maximumM = 2 * significantM;

  if (waterBody !== WaterBody.Coast) {
    if (maximumM < NEGLIGIBLE_INLAND_MAX_WAVE_M) {
      return 'Wind waves stay under 0.3m (1.0ft).';
    }

    return `Wind waves are around ${formatHeight(significantM)}, but be prepared for waves up to ${formatHeight(maximumM)}.`;
  }

  return `Waves are around ${formatHeight(significantM)}, but be prepared for waves up to ${formatHeight(maximumM)}.`;
};

/** The complete description shown under the status word. */
export const getBoatingForecastDescription = (
  conditions: HourlyMarineConditions,
  graphic: VesselGraphic,
  rating: BoatingRiskRating,
  waterBody: WaterBody,
): string => {
  const sentences = [
    getRideCopy(graphic, rating, waterBody),
    getHeightClause(conditions, waterBody),
  ];

  if (waterBody !== WaterBody.Coast) {
    sentences.push(INLAND_FORECAST_CAVEAT);
  }

  return sentences.join(' ');
};
